/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-12
 */

import type {
  DashboardUserSummary,
  DashboardStudio,
  DashboardActivity,
  DashboardQuickAction,
} from '../types/dashboard';

/**
 * Calculate studio ownership metrics for the dashboard.
 * Distinguishes between owned studios vs member studios.
 */
export const calculateStudioOwnershipMetrics = (studios: DashboardStudio[]) => {
  const ownedStudios = studios.filter(studio => studio.role === 'owner');
  const memberStudios = studios.filter(studio => studio.role !== 'owner');

  return {
    totalStudios: studios.length,
    ownedStudios: ownedStudios.length,
    memberStudios: memberStudios.length,
    ownedStudiosList: ownedStudios,
    memberStudiosList: memberStudios,
  };
};

/**
 * Format activity timestamp for display.
 */
export const formatActivityTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }
    if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    }
    return 'Just now';
  } catch {
    return 'Recently';
  }
};

/**
 * Get activity icon based on activity type.
 */
export const getActivityIcon = (activityType: string): string => {
  switch (activityType) {
    case 'login':
      return 'login';
    case 'account_created':
      return 'person_add';
    case 'email_verified':
      return 'check_circle';
    case 'studio_joined':
      return 'business';
    case 'profile_update':
      return 'edit';
    case 'invitation_sent':
      return 'mail';
    case 'invitation_accepted':
      return 'group_add';
    default:
      return 'info';
  }
};

/**
 * Format studio activity for display.
 */
export const formatStudioActivity = (activity: DashboardActivity) => ({
  ...activity,
  formattedTime: formatActivityTimestamp(activity.timestamp),
  icon: activity.icon || getActivityIcon(activity.type),
});

/**
 * Generate studio owner specific quick actions.
 */
export const generateOwnerQuickActions = (
  user: DashboardUserSummary,
  studios: DashboardStudio[],
  backendActions: DashboardQuickAction[]
): DashboardQuickAction[] => {
  const ownershipMetrics = calculateStudioOwnershipMetrics(studios);
  const actions: DashboardQuickAction[] = [...backendActions];

  // Add studio-specific actions for owners
  if (ownershipMetrics.ownedStudios > 0) {
    // If user owns studios, prioritize member management
    actions.unshift({
      type: 'manage_members',
      label: 'Manage Members',
      url: '/studios',
      priority: 'high',
      description: 'Invite and manage your studio team members',
    });
  }

  // Profile completion reminder for studio owners
  if (user.profile_completion < 80) {
    actions.unshift({
      type: 'complete_profile',
      label: 'Complete Profile',
      url: '/profile?mode=edit',
      priority: 'medium',
      description: `Your profile is ${user.profile_completion}% complete`,
    });
  }

  return actions;
};

/**
 * Get user-friendly studio role name.
 */
export const formatStudioRole = (role: string): string => {
  switch (role) {
    case 'owner':
      return 'Owner';
    case 'manager':
      return 'Manager';
    case 'admin':
      return 'Admin';
    case 'instructor':
      return 'Instructor';
    case 'parent':
      return 'Parent';
    case 'student':
      return 'Student';
    case 'member':
      return 'Member';
    default:
      return role.charAt(0).toUpperCase() + role.slice(1);
  }
};

/**
 * Calculate total members across all owned studios.
 */
export const calculateTotalMembersAcrossStudios = (
  studios: DashboardStudio[]
): number =>
  // For now, we don't have member count data in the studio objects
  // This would need to be added to the backend serializer in the future
  // For now, return the number of studios as a placeholder
  studios.filter(studio => studio.role === 'owner').length;

/**
 * Get priority color for quick actions.
 */
export const getQuickActionPriorityColor = (
  priority: string
): 'primary' | 'secondary' | 'warning' | 'error' => {
  switch (priority) {
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
      return 'secondary';
    default:
      return 'primary';
  }
};

/**
 * Format member since date for display.
 */
export const formatMemberSince = (memberSince: string): string => {
  try {
    return memberSince; // Backend already formats this as "Month Year"
  } catch {
    return 'Recently';
  }
};
